import { DestroyRef, DOCUMENT, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { NavigationEnd, Router, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { distinctUntilChanged, filter } from 'rxjs/operators';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { getUnreadMessageCount } from '../../../../infrastructure/base-state/messenger/messenger.selector';

/**
 * Provided by the authenticated shells rather than in root: its subscriptions
 * write the tab title, so they have to end when the shell goes away. Logging
 * out and back in builds a new shell, and a root-scoped instance would keep the
 * old run's subscriptions alive alongside the new one's.
 */
@Injectable()
export class MessengerNotificationService {
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private titleStrategy = inject(TitleStrategy);
  private store = inject(Store);
  private translateService = inject(TranslateService);
  private documentRef = inject(DOCUMENT);

  private _unreadCount: BehaviorSubject<number>;

  public get unreadCount$(): Observable<number> {
    return this._unreadCount.asObservable().pipe(distinctUntilChanged());
  }

  public init(): void {
    this._unreadCount = new BehaviorSubject<number>(0);

    this.router.events
      .pipe(
        filter(event => !!(event instanceof NavigationEnd)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.setUnreadCountInTitle(this._unreadCount.getValue());
      });

    this.store
      .select(getUnreadMessageCount)
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(count => {
        this._unreadCount.next(count);
        this.setUnreadCountInTitle(count);
      });

    /**
     * The count can settle before the translations have loaded, which would
     * leave the raw i18n key in the tab until the next navigation.
     */
    merge(
      this.translateService.onLangChange,
      this.translateService.onTranslationChange
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.setUnreadCountInTitle(this._unreadCount.getValue())
      );
  }

  public complete(): void {
    this._unreadCount.complete();
  }

  /**
   * Decorates the page title with the unread count. The base title is the
   * route's own `title`, resolved through the router's `TitleStrategy` so this
   * agrees with whatever that strategy put in the tab rather than walking the
   * route tree itself.
   */
  private setUnreadCountInTitle(count: number) {
    const titleKey = this.titleStrategy.buildTitle(
      this.router.routerState.snapshot
    );

    // No route in the tree declares a title, so there is no base to decorate.
    if (!titleKey) return;

    const title = this.translateService.instant(titleKey) as string;

    if (count === 0) {
      this.documentRef.title = title;
    } else if (count === 1) {
      this.documentRef.title = `${title} ${<string>(
        this.translateService.instant('messenger.page_title_w_count_single_l')
      )}`;
    } else if (count > 1) {
      this.documentRef.title = `${title} ${<string>(
        this.translateService.instant('messenger.page_title_w_count_multi_l', {
          count
        })
      )}`;
    }
  }
}
